import { gql } from '@apollo/client';

/**
 * User Groups (Group Security v3, collector model) — GraphQL operations for the open-api
 * `user-group.resolver` (research/04-feedback §7.2). Distinct from `security-groups.ts` (the v1
 * scope-group ops, superseded). "Workspace" == Team → the team-scope field is `teamId`; the UI
 * renders it as "Workspace". Enums are UPPERCASE on the wire (DEVICE/ASSET/…). Group visibility
 * (PUBLIC/PRIVATE) is deprecated — every group is account-wide, so that field is no longer selected.
 *
 * Response typing is done with hand-written interfaces at the call sites (no codegen in this repo).
 */

const USER_GROUP_LIST_FIELDS = gql`
  fragment UserGroupListFields on UserGroupListItem {
    _id
    accountId
    name
    description
    color
    isSystem
    viewerCanSee
    teamId
    teamName
    roleIds
    memberCount
    resourceCount
    workspaceCount
    resourceCountByType {
      resourceType
      count
    }
    createdAt
    lastUpdatedDate
  }
`;

export const USER_GROUPS_QUERY = gql`
  query UserGroups($query: QueryUserGroupInput) {
    userGroups(query: $query) {
      ...UserGroupListFields
    }
  }
  ${USER_GROUP_LIST_FIELDS}
`;

export const USER_GROUP_DETAIL_QUERY = gql`
  query UserGroupDetail($id: String!) {
    userGroupDetail(id: $id) {
      _id
      accountId
      name
      description
      color
      teamId
      teamName
      roleIds
      createdBy
      createdAt
      lastUpdatedDate
      memberCount
      memberListWithheld
      grantsWithheld
      memberList {
        _id
        name
        email
        username
        hasMfa
      }
      invites {
        email
        name
        invitedAt
      }
      resourceList {
        resourceType
        resourceId
        resourceName
        resourcePath
        teamId
        teamName
        addedAt
      }
      workspaces {
        teamId
        teamName
        role
        homeFolders {
          type
          groupId
          path
          name
        }
        canEditHomePath
        # Only meaningful on the account row (teamId null). A plain account grant is a role in EVERY
        # teamspace; a capability-only one (written by the v4 migration) admits to none. Both carry a
        # role, so this flag is the only thing that tells them apart — see #2348.
        capabilityOnly
      }
      editCapability {
        groupId
        editCapableDefault
        editTeamIds
        viewOnlyTeamIds
      }
    }
  }
`;

export const WORKSPACE_MEMBERS_QUERY = gql`
  query WorkspaceMembers($workspaceId: String!) {
    workspaceMembers(workspaceId: $workspaceId) {
      _id
      name
      email
      roles
      sources {
        type
        groupId
        groupName
        role
      }
    }
  }
`;

export const WORKSPACE_GROUPS_QUERY = gql`
  query WorkspaceGroups($workspaceId: String!) {
    workspaceGroups(workspaceId: $workspaceId) {
      groupId
      groupName
      role
      memberCount
      addedAt
      homeFolders {
        type
        groupId
        path
        name
      }
      canEditHomePath
    }
  }
`;

// Home-folder picker (§12 per-type): folders of ONE content type in a workspace, via the STANDARD
// `groups` resolver (not a special candidates API). Setting a group's home is admin-only, and admins
// bypass the folder read filter, so this lists ALL folders of that type — paginated, no 100-cap. Only
// the fields the dropdown needs are selected.
export const FOLDER_PICKER_GROUPS_QUERY = gql`
  query FolderPickerGroups($query: QueryGroupInput, $teamId: String, $first: Int) {
    groups(query: $query, teamId: $teamId, first: $first) {
      page {
        edges {
          node {
            _id
            name
            path
            teamId
          }
        }
      }
    }
  }
`;

// `path` lists the IMMEDIATE children of that folder (the tree opens one level at a time);
// `subtree: true` instead returns EVERY descendant of it, which is what selecting a folder needs:
// a folder grant covers its subtree, but access does not inherit at read time, so each descendant
// needs its own stored grant and the picker has to know them all to select them.
//
// `$first` has to be DECLARED here to have any effect. A variable the operation does not declare is
// not an error — the server ignores the extra entry in the variables map and applies the field's own
// default of 100 (`user-group.service.ts` -> `resourceCandidates`). The subtree caller was passing
// `first: 1000` without this line, so it silently read 100 descendants and reported the ceiling as
// 1000. Nothing validates these documents against the schema, so only the declaration proves it.
export const USER_GROUP_RESOURCE_CANDIDATES_QUERY = gql`
  query UserGroupResourceCandidates(
    $teamId: String!
    $resourceType: UserGroupResourceType!
    $search: String
    $folderType: UserGroupResourceType
    $path: String
    $subtree: Boolean
    $first: Int
  ) {
    userGroupResourceCandidates(
      teamId: $teamId
      resourceType: $resourceType
      search: $search
      folderType: $folderType
      path: $path
      subtree: $subtree
      first: $first
    ) {
      items {
        resourceId
        name
        path
        teamId
        hasChildren
        securityGroupIds
        # Teamspace-visible = securities empty. With the group's workspace roles it answers the
        # only question the picker cannot answer alone: would granting this row confer anything, or
        # does the group already reach the folder through its role here (#2348)?
        isTeamspaceVisible
      }
      total
    }
  }
`;

// Model B: objects of a type a group actually grants (securityGroupIds ∋ group), paged + name search.
// Powers the reworked Resources tab. `securityGroupIds` is returned so the FE can remove a grant by
// pulling the group id out of the set and calling SET_RESOURCE_SECURITY_GROUPS. `isEdit` is THIS
// group's level on the row (§4 folder-side half) — it drives the tab's View/Edit column, whose writes
// go through SET_RESOURCE_GROUP_ACCESS (below).
export const GROUP_GRANTED_RESOURCES_QUERY = gql`
  query GroupGrantedResources(
    $groupId: String!
    $resourceType: UserGroupResourceType!
    $search: String
    $first: Int
    $offset: Int
    $folderType: UserGroupResourceType
  ) {
    groupGrantedResources(
      groupId: $groupId
      resourceType: $resourceType
      search: $search
      first: $first
      offset: $offset
      folderType: $folderType
    ) {
      total
      items {
        resourceType
        resourceId
        name
        path
        teamId
        teamName
        securityGroupIds
        isEdit
        folderType
      }
    }
  }
`;

/**
 * Every grant this group carries, across ALL six resource types, in one call.
 *
 * The Permissions tab audits access — so it must not stop at folders. `resourceCount` on the list
 * page already sums all six types, and a table that showed only GROUP rows under-reported against
 * its own badge. The fan-out is server-side (`groupAllGrantedResources`): aliasing the per-type
 * query six times trips `graphql-no-alias` (max 2 calls per field per document).
 */
export const GROUP_ALL_GRANTED_RESOURCES_QUERY = gql`
  query GroupAllGrantedResources($groupId: String!, $first: Int) {
    groupAllGrantedResources(groupId: $groupId, first: $first) {
      total
      items {
        resourceType
        resourceId
        name
        path
        teamId
        teamName
        securityGroupIds
        isEdit
        folderType
      }
    }
  }
`;

export const GROUP_ROLE_USAGE_QUERY = gql`
  query GroupRoleUsage($roleIds: [String!]!) {
    groupRoleUsage(roleIds: $roleIds) {
      roleId
      count
    }
  }
`;

export const RESOURCE_GRANTING_GROUPS_QUERY = gql`
  query ResourceGrantingGroups($resourceType: UserGroupResourceType!, $resourceId: String!) {
    resourceGrantingGroups(resourceType: $resourceType, resourceId: $resourceId) {
      _id
      name
      color
      roleIds
      memberCount
    }
  }
`;

// --- Mutations ---

// Group Security §2 (T910/T912): create is ATOMIC — `input.grants[]` carries ≥1 workspace grant
// `{ teamId, role, homeFolders?, canEditHomePath? }` and the group + its grants
// are written in one mutation (any grant failure fails the whole create, so a teamspace-less orphan
// group — a login-lockout hazard — is unreachable). No best-effort addGroupTo* follow-up loop. Grant
// fields live inside the `CreateUserGroupInput` object, so they're passed via `variables.input`, not
// enumerated in this document. `teamId === ROOT_TEAM_ID` is the account-level grant (backend routes it).
/**
 * Every group's member ids in ONE call (Users page Groups column). Replaces aliasing
 * `userGroupDetail` once per group, which open-api's `graphql-no-alias` caps at 2 per request.
 */
export const USER_GROUP_MEMBERSHIPS_QUERY = gql`
  query UserGroupMemberships {
    userGroupMemberships {
      groupId
      memberIds
    }
  }
`;

export const CREATE_USER_GROUP = gql`
  mutation CreateUserGroup($input: CreateUserGroupInput!) {
    createUserGroup(input: $input) {
      ...UserGroupListFields
    }
  }
  ${USER_GROUP_LIST_FIELDS}
`;

export const UPDATE_USER_GROUP = gql`
  mutation UpdateUserGroup($id: String!, $input: UpdateUserGroupInput!) {
    updateUserGroup(id: $id, input: $input) {
      ...UserGroupListFields
    }
  }
  ${USER_GROUP_LIST_FIELDS}
`;

export const DELETE_USER_GROUP = gql`
  mutation DeleteUserGroup($id: String!) {
    deleteUserGroup(id: $id) {
      success
      affectedMembers
      removedResourceGrants
      removedWorkspaceEntries
    }
  }
`;

export const ADD_USER_GROUP_MEMBERS = gql`
  mutation AddUserGroupMembers($groupId: String!, $userIds: [String!]!) {
    addUserGroupMembers(groupId: $groupId, userIds: $userIds) {
      count
    }
  }
`;

export const REMOVE_USER_GROUP_MEMBERS = gql`
  mutation RemoveUserGroupMembers($groupId: String!, $userIds: [String!]!) {
    removeUserGroupMembers(groupId: $groupId, userIds: $userIds) {
      count
    }
  }
`;

// §12 group-only — revoke a PENDING email invite off the group (userGroups.invites[]).
export const REVOKE_GROUP_INVITE = gql`
  mutation RevokeGroupInvite($groupId: String!, $email: String!) {
    revokeGroupInvite(groupId: $groupId, email: $email) {
      success
    }
  }
`;

export const ADD_USER_GROUP_RESOURCES = gql`
  mutation AddUserGroupResources($groupId: String!, $resources: [ResourceRefInput!]!) {
    addUserGroupResources(groupId: $groupId, resources: $resources) {
      count
    }
  }
`;

export const REMOVE_USER_GROUP_RESOURCES = gql`
  mutation RemoveUserGroupResources($groupId: String!, $resources: [ResourceRefInput!]!) {
    removeUserGroupResources(groupId: $groupId, resources: $resources) {
      count
    }
  }
`;

/**
 * Set which security groups a folder/item is shared to (Group Security Model B). Grants live on the
 * object's `securityGroupIds`. `groupIds`: null ⇒ inherit the folder's grant (unset), [] ⇒ shared to
 * nobody (per-item unshare), [ids] ⇒ explicit share. resourceType 'GROUP' = folder.
 */
export const SET_RESOURCE_SECURITY_GROUPS = gql`
  mutation SetResourceSecurityGroups(
    $resourceType: UserGroupResourceType!
    $resourceId: String!
    $groupIds: [String!]
  ) {
    setResourceSecurityGroups(
      resourceType: $resourceType
      resourceId: $resourceId
      groupIds: $groupIds
    ) {
      success
    }
  }
`;

/**
 * Set ONE group's access level (View/Edit) on a folder/item — the Resources tab's View/Edit column and
 * the add-resources "grant as" choice. UPSERTS this group's entry in the object's `securityGroupIds`
 * and leaves every other group's grant alone, so it is also the correct ADD path (no separate union
 * write is needed, and a new grant never lands at an unspecified level).
 *
 * §4 role cap: `isEdit` is only the FOLDER-SIDE half. Effective edit is
 * `isEdit && the group's role in that resource's teamspace allows edit` — setting Edit on a
 * viewer-role group persists the intent but still resolves view-only.
 */
export const SET_RESOURCE_GROUP_ACCESS = gql`
  mutation SetResourceGroupAccess(
    $resourceType: UserGroupResourceType!
    $resourceId: String!
    $groupId: String!
    $isEdit: Boolean!
  ) {
    setResourceGroupAccess(
      resourceType: $resourceType
      resourceId: $resourceId
      groupId: $groupId
      isEdit: $isEdit
    ) {
      success
    }
  }
`;

// #2166: a grant is a ROLE (+ optional explicit home folders). The auto-create args
// (`autoCreateFolderTypes` / the older `autoCreateHomeFolder`) were removed from the API — adding a
// group to a teamspace never creates a folder.
export const ADD_GROUP_TO_WORKSPACE = gql`
  mutation AddGroupToWorkspace(
    $workspaceId: String!
    $groupId: String!
    $role: String!
    $homeFolders: [GroupHomeFolderInput!]
    $canEditHomePath: Boolean
  ) {
    addGroupToWorkspace(
      workspaceId: $workspaceId
      groupId: $groupId
      role: $role
      homeFolders: $homeFolders
      canEditHomePath: $canEditHomePath
    ) {
      success
    }
  }
`;

export const UPDATE_GROUP_WORKSPACE_ROLE = gql`
  mutation UpdateGroupWorkspaceRole($workspaceId: String!, $groupId: String!, $role: String!) {
    updateGroupWorkspaceRole(workspaceId: $workspaceId, groupId: $groupId, role: $role) {
      success
    }
  }
`;

export const REMOVE_GROUP_FROM_WORKSPACE = gql`
  mutation RemoveGroupFromWorkspace($workspaceId: String!, $groupId: String!) {
    removeGroupFromWorkspace(workspaceId: $workspaceId, groupId: $groupId) {
      success
    }
  }
`;

// #2166 — same as addGroupToWorkspace above: no auto-create args.
export const ADD_GROUP_TO_ACCOUNT = gql`
  mutation AddGroupToAccount(
    $groupId: String!
    $role: String!
    $homeFolders: [GroupHomeFolderInput!]
    $canEditHomePath: Boolean
  ) {
    addGroupToAccount(
      groupId: $groupId
      role: $role
      homeFolders: $homeFolders
      canEditHomePath: $canEditHomePath
    ) {
      success
    }
  }
`;

export const REMOVE_GROUP_FROM_ACCOUNT = gql`
  mutation RemoveGroupFromAccount($groupId: String!) {
    removeGroupFromAccount(groupId: $groupId) {
      success
    }
  }
`;

/**
 * §7 (T960/T961) — retire the "turn on Groups" prompt. ACCOUNT-scoped, not per-user (spec §7): the
 * backend stamps `company.featureData.groupsPromo.dismissedAt`, so the prompt never returns for
 * anyone in the account. No args — the account comes from the auth context. Boolean result, matching
 * the other dismiss-a-prompt mutation shape (`dismissPlatformCleanup`).
 * The READ side needs no operation: `me.featureData` is the company's featureData as a JSON scalar,
 * so `groupsPromo` rides the existing ME_QUERY (see GroupsPromoBanner).
 */
export const DISMISS_GROUPS_PROMO = gql`
  mutation DismissGroupsPromo {
    dismissGroupsPromo
  }
`;

/**
 * "Activate Groups" — the account switch, read + write, for the toggle on the Groups page
 * (Ted, 2026-09-02).
 *
 * `groupsPromo.planEligible` is the server's own `isGroupSecurityPlanEligible` verdict — the SAME
 * helper the write gate uses — so the frontend never re-derives the plan ladder (the grandfather
 * stamp, super-user, `isEnterprise` and the Pro Plus minimum all stay server-side).
 * `shouldPrompt` / dismissal belong to the promo BANNER, not the toggle, and are deliberately unread:
 * a switch has to render regardless of whether someone dismissed a banner.
 */
export const GROUPS_ACTIVATION_QUERY = gql`
  query GroupsActivation {
    groupsPromo {
      planEligible
    }
  }
`;

/**
 * The ONLY write path for `company.feature.groupSecurity` (deliberately absent from
 * `CompanyFeatureInput`, so the generic `updateCompanyInfo` cannot set it). Owner / Super Admin only;
 * ACTIVATING requires Pro Plus and throws GROUPS_REQUIRES_PRO_PLUS below it, while deactivating is
 * always allowed. Does NOT touch `feature.folderLevelSecurity` — that is folder security, a
 * different and lower (Pro) entitlement.
 */
export const SET_GROUP_SECURITY_ENABLED = gql`
  mutation SetGroupSecurityEnabled($enabled: Boolean!) {
    setGroupSecurityEnabled(enabled: $enabled) {
      _id
      feature {
        groupSecurity
      }
    }
  }
`;
